import { Head, Link } from "@inertiajs/react"
import { Paperclip } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { FeedbackStatusBadge } from "@/components/feedback/FeedbackStatusBadge"
import { PageHeader } from "@/components/PageHeader"
import { TableCard } from "@/components/blocks/TableCard"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_STATUSES,
  type FeedbackCategory,
  type FeedbackStatus,
  type FeedbackSummary,
} from "@/types/feedback"

type Props = {
  feedbacks: FeedbackSummary[]
  counts: Record<FeedbackStatus, number>
  status: FeedbackStatus | null
  category: FeedbackCategory | null
}

function href(status: FeedbackStatus | null, category: FeedbackCategory | null) {
  const query = new URLSearchParams()
  if (status) query.set("status", status)
  if (category) query.set("category", category)
  const string = query.toString()
  return string ? `/admin/feedbacks?${string}` : "/admin/feedbacks"
}

export default function AdminFeedbacksIndex({ feedbacks, counts, status, category }: Props) {
  const total = FEEDBACK_STATUSES.reduce((sum, s) => sum + counts[s.value], 0)
  const filters: { value: FeedbackStatus | null; label: string; count: number }[] = [
    { value: null, label: "All", count: total },
    ...FEEDBACK_STATUSES.map((s) => ({ value: s.value, label: s.label, count: counts[s.value] })),
  ]

  return (
    <>
      <Head title="Feedback">
        <meta name="description" content="Review problems users have reported from the feedback form." />
        <meta property="og:title" content="Feedback" />
        <meta property="og:description" content="Review problems users have reported from the feedback form." />
      </Head>
      <AdminShell>
        <PageHeader
          title="Feedback"
          description="Problems users reported from the feedback form on any page."
          tabs={
            <nav aria-label="Filter by status" className="flex flex-wrap items-end gap-6 border-b border-hairline">
              {filters.map((filter) => {
                const active = filter.value === status
                return (
                  <Link
                    key={filter.label}
                    href={href(filter.value, category)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "-mb-px border-b-2 px-1 py-3 text-sm no-underline",
                      active
                        ? "border-accent font-medium text-accent-display"
                        : "border-transparent text-ink-body hover:text-ink-display",
                    )}
                  >
                    {filter.label} <span className="text-ink-muted">{filter.count}</span>
                  </Link>
                )
              })}
            </nav>
          }
        />

        <nav aria-label="Filter by category" className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink-muted">Category</span>
          {[{ value: null, label: "Any" }, ...FEEDBACK_CATEGORIES].map((option) => {
            const active = option.value === category
            return (
              <Link
                key={option.label}
                href={href(status, option.value)}
                aria-current={active ? "true" : undefined}
                className="no-underline"
              >
                <Badge tone={active ? "accent" : "muted"}>{option.label}</Badge>
              </Link>
            )
          })}
        </nav>

        {feedbacks.length === 0 ? (
          <div className="callout mt-6">
            <p>{status || category ? "No feedback matches these filters." : "No feedback yet."}</p>
          </div>
        ) : (
          <div className="mt-6">
            <TableCard>
              <Table bleed rowLinks className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
                <TableHead>
                  <TableRow>
                    <TableHeader>Feedback</TableHeader>
                    <TableHeader className="hidden md:table-cell">Category</TableHeader>
                    <TableHeader className="text-right">Status</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedbacks.map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell>
                        <Link
                          href={`/admin/feedbacks/${feedback.id}`}
                          className="block max-w-xl no-underline after:absolute after:inset-0 hover:text-ink-display"
                        >
                          <span className="flex items-center gap-2">
                            <span className="truncate font-medium text-ink-display">{feedback.message}</span>
                            {feedback.has_screenshot && (
                              <Paperclip className="size-4 shrink-0 text-ink-muted" aria-label="Has a screenshot" />
                            )}
                          </span>
                          <span className="block truncate text-xs text-ink-muted">
                            {feedback.user_email ?? "Signed out"} · {feedback.page_path ?? "Unknown page"} ·{" "}
                            {formatDate(feedback.created_at)}
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge tone="neutral">{categoryLabel(feedback.category)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <FeedbackStatusBadge status={feedback.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableCard>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function categoryLabel(category: FeedbackCategory) {
  return FEEDBACK_CATEGORIES.find((c) => c.value === category)?.label ?? category
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
}
